import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardHeader, CardContent, LinearProgress, Alert,
  Grid, Typography, Stack, Chip, IconButton, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  TextField, MenuItem, Select, InputLabel, FormControl,
  Paper, Checkbox, FormControlLabel, Tooltip
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { authApi } from '../../utils/apiClient';

const ROLE_COLOR = { Admin: 'secondary', Manager: 'success', Seller: 'primary', Participant: 'warning' };

const AdminRoles = () => {
  const [selectedRole, setSelectedRole] = useState('');
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permissions: {}
  });
  const [availableRoles, setAvailableRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState(null);
  const [updateMessage, setUpdateMessage] = useState('');
  const [updateError, setUpdateError] = useState('');

  // Define permissions by category - using actual database permissions
  const permissions = {
    'User Management': [
      { key: 'view_users', label: 'View Users', description: 'View user profiles and information' },
      { key: 'manage_users', label: 'Manage Users', description: 'Create, edit, and manage user accounts' }
    ],
    'Auction Management': [
      { key: 'view_auctions', label: 'View Auctions', description: 'View auction listings and details' },
      { key: 'create_auction', label: 'Create Auction', description: 'Create new auction listings' },
      { key: 'update_auction', label: 'Update Auction', description: 'Modify auction details and settings' },
      { key: 'delete_auction', label: 'Delete Auction', description: 'Remove auction listings' },
      { key: 'join_auction_observer', label: 'Join as Observer', description: 'Join auctions as an observer' },
      { key: 'join_auction_participant', label: 'Join as Participant', description: 'Join auctions as a participant' },
      { key: 'place_bid', label: 'Place Bid', description: 'Place bids in auctions' },
      { key: 'place_secret_bid', label: 'Place Secret Bid', description: 'Place secret bids in auctions' }
    ],
    'Product Management': [
      { key: 'view_products', label: 'View Products', description: 'View product listings and details' },
      { key: 'create_product', label: 'Create Product', description: 'Add new products to the catalog' },
      { key: 'update_product', label: 'Update Product', description: 'Modify product information and settings' },
      { key: 'delete_product', label: 'Delete Product', description: 'Remove products from the catalog' },
      { key: 'update_own_product', label: 'Update Own Product', description: 'Modify own product information' },
      { key: 'delete_own_product', label: 'Delete Own Product', description: 'Remove own products from the catalog' }
    ],
    'Second Hand Management': [
      { key: 'view_secondhand', label: 'View Second Hand', description: 'View second-hand listings' },
      { key: 'create_secondhand', label: 'Create Second Hand', description: 'Create second-hand listings' },
      { key: 'update_secondhand', label: 'Update Second Hand', description: 'Modify second-hand details' },
      { key: 'delete_secondhand', label: 'Delete Second Hand', description: 'Remove second-hand listings' },
      { key: 'update_own_secondhand', label: 'Update Own Second Hand', description: 'Modify own second-hand listings' },
      { key: 'delete_own_secondhand', label: 'Delete Own Second Hand', description: 'Remove own second-hand listings' }
    ],
    'Review Management': [
      { key: 'view_reviews', label: 'View Reviews', description: 'View product and auction reviews' },
      { key: 'create_review', label: 'Create Review', description: 'Create reviews for products and auctions' },
      { key: 'moderate_reviews', label: 'Moderate Reviews', description: 'Moderate and manage reviews' }
    ],
    'Financial Management': [
      { key: 'view_transactions', label: 'View Transactions', description: 'View financial transactions and reports' },
      { key: 'manage_transactions', label: 'Manage Transactions', description: 'Manage financial transactions' },
      { key: 'view_analytics', label: 'View Analytics', description: 'Access analytics and reports' },
      { key: 'view_all_analytics', label: 'View All Analytics', description: 'Access all analytics data' }
    ],
    'Communication': [
      { key: 'send_message', label: 'Send Message', description: 'Send messages to other users' },
      { key: 'view_messages', label: 'View Messages', description: 'View message history' },
      { key: 'create_notification', label: 'Create Notification', description: 'Create system notifications' },
      { key: 'view_notifications', label: 'View Notifications', description: 'View system notifications' }
    ],
    'System Administration': [
      { key: 'manage_permissions', label: 'Manage Permissions', description: 'Configure role-based permissions' },
      { key: 'system_admin', label: 'System Admin', description: 'Full system administration access' },
      { key: 'view_all_data', label: 'View All Data', description: 'Access all system data' },
      { key: 'view_own_data', label: 'View Own Data', description: 'Access own user data' },
      { key: 'view_own_analytics', label: 'View Own Analytics', description: 'Access own analytics data' }
    ],
    'Bidding Management': [
      { key: 'view_bids', label: 'View Bids', description: 'View bid history and details' },
      { key: 'view_secret_bids', label: 'View Secret Bids', description: 'View secret bid information' }
    ],
    'User Features': [
      { key: 'favorite_products', label: 'Favorite Products', description: 'Add products to favorites' }
    ]
  };



  const handleRoleSelect = (roleValue) => {
    setSelectedRole(roleValue);
    if (roleValue) {
      const role = availableRoles.find(r => r.role === roleValue);
      if (role) {
        setRoleForm({
          name: role.role,
          description: role.description || '',
          permissions: role.permissions.reduce((acc, perm) => {
            acc[perm] = true;
            return acc;
          }, {})
        });
      }
    }
  };

  const openRoleModal = (role = null) => {
    setEditingRole(role);
    setUpdateMessage('');
    setUpdateError('');
    if (role) {
      setRoleForm({
        name: role.role,
        description: role.description || '',
        permissions: role.permissions.reduce((acc, perm) => {
          acc[perm] = true;
          return acc;
        }, {})
      });
    } else {
      setRoleForm({
        name: '',
        description: '',
        permissions: {}
      });
    }
    setShowRoleModal(true);
  };

  const closeRoleModal = () => {
    setShowRoleModal(false);
    setEditingRole(null);
    setRoleForm({
      name: '',
      description: '',
      permissions: {}
    });
    setUpdateMessage('');
    setUpdateError('');
  };

  const handleRoleFormChange = (e) => {
    const { name, value, checked } = e.target;
    
    if (name.startsWith('permissions.')) {
      const permissionKey = name.replace('permissions.', '');
      setRoleForm(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          [permissionKey]: checked
        }
      }));
    } else {
      setRoleForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleRoleFormSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setUpdateError('');
      setUpdateMessage('');
      
      if (editingRole) {
        // Update existing role
        const permissions = Object.keys(roleForm.permissions).filter(key => roleForm.permissions[key]);
        
        const response = await authApi.updateRole(editingRole._id, {
          permissions,
          description: roleForm.description
        });
        
        if (response.success) {
          // Refresh roles list
          const rolesResponse = await authApi.roles();
          if (rolesResponse.success) {
            setAvailableRoles(rolesResponse.roles);
          }
          setUpdateMessage('Role updated successfully!');
          setTimeout(() => {
            closeRoleModal();
            setUpdateMessage('');
          }, 1500);
        } else {
          setUpdateError(response.message || 'Failed to update role');
        }
      } else {
        // Create new role (not implemented yet)
        closeRoleModal();
      }
    } catch (error) {
      console.error('Error saving role:', error);
      setUpdateError('An error occurred while updating the role');
    }
  };

  // Fetch roles from database
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setRolesLoading(true);
        const response = await authApi.roles();
        if (response.success) {
          setAvailableRoles(response.roles);
        } else {
          setRolesError('Failed to load roles');
        }
      } catch (error) {
        console.error('Error fetching roles:', error);
        setRolesError('Failed to load roles');
      } finally {
        setRolesLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const getPermissionCount = (roleValue) => {
    const role = availableRoles.find(r => r.role === roleValue);
    return role ? role.permissions.length : 0;
  };

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0px 16px 45px rgba(15, 23, 42, 0.12)' }}>
      <CardHeader
        title="Role & Permissions Management"
        subheader="Configure role-based access control for the platform"
        titleTypographyProps={{ variant: 'h5', fontWeight: 700 }}
        action={
          <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => openRoleModal()} sx={{ borderRadius: 2 }}>
            Add New Role
          </Button>
        }
      />
      {rolesLoading && <LinearProgress color="primary" />}
      <CardContent sx={{ pt: 0 }}>
        {rolesError && <Alert severity="error" sx={{ mb: 2 }}>{rolesError}</Alert>}

        {/* Role Selection */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 3 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Select Role to Manage</InputLabel>
            <Select
              value={selectedRole}
              label="Select Role to Manage"
              onChange={e => handleRoleSelect(e.target.value)}
            >
              <MenuItem value=""><em>Choose a role…</em></MenuItem>
              {availableRoles.map(role => (
                <MenuItem key={role.role} value={role.role}>
                  {role.role} — {role.description || 'No description'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {/* Selected Role Permissions Panel */}
        {selectedRole && (() => {
          const role = availableRoles.find(r => r.role === selectedRole);
          return role ? (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {role.role} Permissions
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label={`${role.permissions.length} permissions`} size="small" />
                  <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => openRoleModal(role)}>
                    Edit Role
                  </Button>
                </Stack>
              </Stack>
              <Grid container spacing={1}>
                {role.permissions.map(perm => (
                  <Grid item key={perm}>
                    <Chip label={perm} size="small" color="primary" variant="outlined" />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          ) : null;
        })()}

        {/* All Roles Grid */}
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>All Roles</Typography>
        <Grid container spacing={2}>
          {availableRoles.map(role => (
            <Grid item xs={12} sm={6} md={4} key={role.role}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>{role.role}</Typography>
                  <IconButton size="small" color="primary" onClick={() => openRoleModal(role)} title="Edit">
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  {role.description || 'No description'}
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">{role.permissions.length} permissions</Typography>
                  <Chip label={role.role} size="small" color={ROLE_COLOR[role.role] || 'default'} />
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </CardContent>

      {/* Role Edit Dialog */}
      <Dialog open={showRoleModal} onClose={closeRoleModal} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminPanelSettingsOutlinedIcon />
            {editingRole ? 'Edit Role' : 'Add New Role'}
          </Box>
          <IconButton onClick={closeRoleModal} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <form onSubmit={handleRoleFormSubmit}>
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Role Name" name="name" value={roleForm.name} onChange={handleRoleFormChange} required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Description" name="description" value={roleForm.description} onChange={handleRoleFormChange} />
                </Grid>
              </Grid>

              <Divider />
              <Typography variant="subtitle1" fontWeight={700}>Permissions</Typography>

              <Grid container spacing={2}>
                {Object.entries(permissions).map(([category, perms]) => (
                  <Grid item xs={12} sm={6} key={category}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {category}
                      </Typography>
                      <Stack spacing={0.5}>
                        {perms.map(permission => (
                          <Tooltip key={permission.key} title={permission.description} placement="right">
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  name={`permissions.${permission.key}`}
                                  checked={roleForm.permissions?.[permission.key] || false}
                                  onChange={handleRoleFormChange}
                                />
                              }
                              label={<Typography variant="body2">{permission.label}</Typography>}
                              sx={{ m: 0 }}
                            />
                          </Tooltip>
                        ))}
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {updateMessage && <Alert severity="success">{updateMessage}</Alert>}
              {updateError && <Alert severity="error">{updateError}</Alert>}
            </Stack>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeRoleModal} variant="outlined" color="inherit">Cancel</Button>
            <Button type="submit" variant="contained">
              {editingRole ? 'Update Role' : 'Add Role'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Card>
  );
};

export default AdminRoles;
